import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { FilterParser } from '@core/filter-parser';
import { Notes, CreateNote, IFileUpload, UpdateNote } from '@core/model/note/note';
import { PageFilter } from '@core/model/pagination/filter';
import { PageResult } from '@core/model/pagination/page-result';
import { LocalStorageDataService } from '@core/services/local-storage.service';
import { strictMap } from '@core/utils/object-utils';
import { GetPaginatedNotesDataGQL, GetNotesdetailGQL, NotesWithTicketStatusGQL, GetPaginatedNotesNotGQL, GetNotesDetailNotGQL, GetNoteByObjectIdGQL, NoteDetailsForBusinessGQL, Attachment } from '@generated/queries';
import { BaseService } from '@shared/services/base.service';
import { plainToInstance } from 'class-transformer';
import { firstValueFrom, map } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class NotesService {

  routUrl: string;

  constructor(
    private http: HttpClient,
    private baseService: BaseService,
    private paginatedNotes: GetPaginatedNotesDataGQL,
    private notesDetails: GetNotesdetailGQL,
    private noteWithTicketStatusGet: NotesWithTicketStatusGQL,
    private paginatedNotesNot: GetPaginatedNotesNotGQL,
    private notesDetailNot: GetNotesDetailNotGQL,
    private noteByObjectIdGet: GetNoteByObjectIdGQL,
    private localStorageService: LocalStorageDataService,
    private getNoteDetails: NoteDetailsForBusinessGQL
  ) {
    this.routUrl = this.baseService.getBaseUrl();
  }
  async addNote(model: Notes, files: File[], mentionUserIdList: string[]) {
    
    const routUrl = `${this.routUrl}/MongoNotes/create`;
    const httpOptions = {
      headers: new HttpHeaders({
        type: 'json',
      }),
    };
    const tokenHeaders = await this.baseService.transformOptions(httpOptions);
    const formData = new FormData();

    for (const file of files) {
      const breakdown = model;
      formData.append(breakdown.objectID, file);
    }

    const noteModel = strictMap(CreateNote, model);
    if (mentionUserIdList && mentionUserIdList.length > 0) {
      noteModel.mentionUserIdList = mentionUserIdList;
    }
    const vm = JSON.stringify(noteModel);
    formData.append('model', vm);
    return firstValueFrom(this.http.post(routUrl, formData, tokenHeaders));
  }

async addTicketNote(model: Notes, files: File[], mentionUserIdList?: string[]) {
  const routUrl = `${this.routUrl}/MongoNotes/createticketnote`;

  // Prepare headers
  const httpOptions = {
    headers: new HttpHeaders({
      type: 'json',
    }),
  };
  const tokenHeaders = await this.baseService.transformOptions(httpOptions);

  // Initialize FormData
  const formData = new FormData();

  // Validate model.objectID before appending files
  if (!model?.objectID) {
    throw new Error('The model.objectID is required to upload files.');
  }

  // Append files to formData safely
  if (files && files.length > 0) {
    for (const file of files) {
      formData.append(model.objectID, file);
    }
  }

  // Map model to view model
  const noteModel = strictMap(CreateNote, model);

  // Include mention user IDs if present
  if (mentionUserIdList && mentionUserIdList.length > 0) {
    noteModel.mentionUserIdList = mentionUserIdList;
  }

  // Append note model as JSON string
  const vm = JSON.stringify(noteModel);
  formData.append('model', vm);

  // Send POST request and return promise
  return firstValueFrom(this.http.post(routUrl, formData, tokenHeaders));
}


  async updateNote(note: Notes, lstUploadedFiles: IFileUpload[], existingUploadedFiles: IFileUpload[], files: File[] = []) {
    const routUrl = `${this.routUrl}/MongoNotes/update`;
    const httpOptions = {
      headers: new HttpHeaders({
        type: 'json',
      }),
    };
    const tokenHeaders = await this.baseService.transformOptions(httpOptions);

    const filestoDelete: IFileUpload[] = existingUploadedFiles.filter(
      (existingFile) =>
        !lstUploadedFiles.some(
          (lstFile) => lstFile.AttachmentName === existingFile.AttachmentName
        )
    );

    const formData = new FormData();

    if (!note?.objectID) {
      throw new Error('The note.objectID is required to upload files.');
    }

    for (const file of files ?? []) {
      formData.append(note.objectID, file);
    }

    filestoDelete.forEach((el) => {
      formData.append('blobFileName', el.AttachmentBlobName.toString());
    });

    const vm = JSON.stringify(strictMap(UpdateNote, note));
    formData.append('model', vm);
    return firstValueFrom(this.http.post(routUrl, formData, tokenHeaders));
  }

  async addFiles(model: Attachment, files: File[]) {
    const routUrl = `${this.routUrl}/MongoNotes/insertFiles`;
    const httpOptions = {
      headers: new HttpHeaders({
        type: 'json',
      }),
    };
    const tokenHeaders = await this.baseService.transformOptions(httpOptions);
    const formData = new FormData();

    const breakdown = model;
    for (const file of files) {
      formData.append(breakdown.id, file);
    }
    const noteModel = strictMap(Attachment, model);
    const vm = JSON.stringify(noteModel);
    formData.append('model', vm);
    return firstValueFrom(this.http.post(routUrl, formData, tokenHeaders));
  }

  getPaginatedNotes(filter: PageFilter, objectId: string,noteTypeId: string): Promise<PageResult<Notes>> {

    const paginationFilter = FilterParser.parsePageFilter(filter);
    return firstValueFrom(this.paginatedNotes
      .fetch(
        {
          skip: paginationFilter.skip,
          take: paginationFilter.take,
          moduleId:objectId,
          noteTypeId: noteTypeId,
        },
        {
          fetchPolicy: 'network-only'
        }
      )
      // eslint-disable-next-line
      .pipe(map((x) => new PageResult<Notes>(x.data.session.paginatedNotesDetails as any))));

  }

  getPaginatedNotesNot(filter: PageFilter, objectId: string): Promise<PageResult<Notes>> {
    const paginationFilter = FilterParser.parsePageFilter(filter);

    return firstValueFrom(this.paginatedNotesNot
      .fetch(
        {
          skip: paginationFilter.skip,
          take: paginationFilter.take,
          moduleId:objectId,

        },
        {
          fetchPolicy: 'network-only',
        }
      )
      // eslint-disable-next-line
      .pipe(map((x) => new PageResult<Notes>(x.data.session.paginatedNotesDetails as any))))
  }

  getNotesDetails(filter: PageFilter, objectId: string,noteTypeId: string): Promise<Notes[]> {
    FilterParser.parsePageFilter(filter);

    return firstValueFrom(this.notesDetails
      .fetch(
        {
          moduleId:objectId,
          noteTypeId: noteTypeId,
        },
        {
          fetchPolicy: 'network-only'
        }
      )
      .pipe(map((x) => plainToInstance(Notes,x.data.session.notesDetails as []))))
  }

  getNotesDetailsNot(filter: PageFilter, objectId: string): Promise<Notes[]> {
    FilterParser.parsePageFilter(filter);

    return firstValueFrom(this.notesDetailNot
      .fetch(
        {
          moduleId:objectId,

        },
        {
          fetchPolicy: 'network-only',
        }
      )
      .pipe(map((x) => plainToInstance(Notes,x.data.session.notesDetails))))
  }

  getNotesByObjectId(objectId: string, noteType?: string): Promise<PageResult<Notes>> {

    return firstValueFrom(this.noteByObjectIdGet
      .fetch(
        {
          moduleId:objectId,
          noteType: noteType
        },
        {
          fetchPolicy: this.localStorageService.getFetchPolicy('note'),
        }
      )
      // eslint-disable-next-line
      .pipe(map((x) => new PageResult<Notes>(x.data.session.paginatedNotesDetails as any))))
  }

  notesWithTicketStatus(filter: PageFilter, objectId: string): Promise<Notes[]> {
    FilterParser.parsePageFilter(filter);
    return firstValueFrom(this.noteWithTicketStatusGet
      .fetch(
        {
          moduleId:objectId,

        },
        {
          fetchPolicy: 'network-only'
        }
      )
      .pipe(map((x) => plainToInstance(Notes, x.data.session.notesWithTicketStatus))))
  }

  notesDetailsForBusiness(): Promise<Notes[]>{

    return firstValueFrom(this.getNoteDetails
      .fetch(
        {

        },
        {
          fetchPolicy: 'network-only'
        }
      )
      .pipe(
        map((x) => plainToInstance(Notes, x.data.session.notesDetailsForBusiness))
      ))
  }

  downloadMyFile(myLink:string){
      const link = document.createElement('a');
      link.setAttribute('target', '_blank');
      link.setAttribute('href', myLink);
      link.setAttribute('download', `img.png`);
      document.body.appendChild(link);
      link.click();
      link.remove();
  }

  downloadFile(blobFileName: string): void {
    const route = blobFileName;
    this.http.get(route, { responseType: 'blob' }).subscribe({
      next: (result: Blob) => {
        const blob = new Blob([result]);
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = blobFileName;
        a.click();
        window.URL.revokeObjectURL(url);
      },
      error: (error) => {
        console.error('Error downloading file:', error);
        // Handle error appropriately here
      }
    });
  }

}
